// video.js — 液晶に流す映像の再生管理
//
// <video> は canvas に直接 drawImage できるので、盤面の液晶部分に
// そのまま合成する。映像を持たない機種は今まで通りコードで描く演出のまま。
//
// 注意点:
//  - 自動再生の制約があるので必ず muted + playsinline。音はこのアプリの
//    WebAudio 側で鳴らすため、映像の音声は使わない。
//  - 状態が切り替わるたびに読み込み直すと間に合わないので、全部先に作って
//    表示するものだけ再生する。

import { assetUrl } from './i18n.js';

const STATES = ['idle', 'spin', 'reach', 'jackpot'];

// すでに行き先が決まっているもの（アップロードされた Blob など）はそのまま。
// 同梱の素材だけは、言語ディレクトリのぶん深くなった位置を戻してやる。
const resolve = (src) => (/^(?:blob:|data:|https?:|\/)/i.test(src) ? src : assetUrl(src));

export class VideoDeck {
  /**
   * @param {object} sources 状態名 → ファイルパス
   */
  constructor(sources) {
    this.sources = sources || {};
    this.els = new Map();
    this.state = null;
    this.ready = false;
    this.failed = false;
    this._lastDrawn = null;
    this._make();
  }

  _make() {
    for (const name of STATES) {
      const src = this.sources[name];
      if (!src) continue;
      // 同じファイルを複数状態で使い回すことがあるので要素も共有する
      const existing = [...this.els.entries()].find(([, v]) => v.dataset.src === src);
      if (existing) { this.els.set(name, existing[1]); continue; }

      const v = document.createElement('video');
      v.src = resolve(src);
      v.dataset.src = src;
      v.muted = true;
      v.defaultMuted = true;
      v.loop = true;
      v.playsInline = true;
      // 最初は metadata だけ。全部を auto で読むと数十 MB を同時に取りに行き、
      // どれも再生可能にならない（実測: 3 本同時だと readyState が 1 で頭打ち）。
      v.preload = 'metadata';
      // crossOrigin は付けない。同一オリジンなので canvas は汚染されず、
      // 付けると CORS ヘッダを要求して読み込みに失敗する。
      v.addEventListener('error', () => { this.failed = true; }, { once: true });
      v.addEventListener('canplay', () => { this.ready = true; }, { once: true });
      v.load();
      this.els.set(name, v);
    }
  }

  /** 状態を切り替える。同じ要素を使い回す場合は頭出しし直す */
  setState(name, { restart = false } = {}) {
    if (!this.els.size) return;
    const next = this.els.get(name) || this.els.get('idle');
    if (!next) return;

    const prev = this.state ? this.els.get(this.state) : null;
    if (prev && prev !== next) prev.pause();
    this.state = name;

    // 頭出しはデータが来てからにする。readyState 0 の要素に currentTime を
    // 代入するとシークが完了せず、全部バッファし終えても readyState が 1 の
    // まま固まる（実測）。まだ読めていないなら位置は 0 なので何もしなくてよい。
    if ((restart || prev !== next) && next.readyState >= 2 && next.currentTime > 0.05) {
      try { next.currentTime = 0; } catch { /* 念のため */ }
    }
    this._warm(next);
    this._play(next);
  }

  // 実際に使う段になってから本体を読みに行く。
  // ここで load() を呼んではいけない。読み込み中の要素を作り直すことになり、
  // 全体をバッファし終えても readyState が 1 のまま固まる（実測）。
  // preload を上げるだけでブラウザが続きを取りに行く。
  _warm(v) {
    if (!v || v.dataset.warm) return;
    v.dataset.warm = '1';
    v.preload = 'auto';
  }

  /**
   * 次に使いそうなものを先読みしておく。
   * 今流しているものが再生可能になってから 1 本ずつ、帯域を食い合わせない。
   */
  prefetchNext() {
    const cur = this.current;
    if (!cur || cur.readyState < 3) return;
    for (const v of new Set(this.els.values())) {
      if (!v.dataset.warm) { this._warm(v); return; }
    }
  }

  _play(v) {
    if (!v) return;
    const p = v.play();
    // ユーザー操作前だと拒否されることがある。次の操作で再度呼ばれるので握りつぶす
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }

  resume() { if (this.state) this._play(this.els.get(this.state)); }

  pause() {
    for (const v of new Set(this.els.values())) v.pause();
  }

  get current() {
    return this.state ? this.els.get(this.state) : null;
  }

  /** 描ける状態か（読み込み中は下地だけ描いてもらう） */
  get drawable() {
    const v = this.current;
    return !!v && v.readyState >= 2 && v.videoWidth > 0;
  }

  /**
   * 指定した矩形に cover（短辺を合わせて中央を切り抜き）で描く。
   * 液晶は 4:3 に近く、素材は 16:9 なので左右が切れる。
   */
  draw(ctx, x, y, w, h) {
    let v = this.current;
    // 切り替えた先がまだ読めていないときは、直前に映っていたものを出し続ける。
    // ここで諦めると液晶が一瞬真っ黒になって目立つ。
    if (!v || v.readyState < 2 || !v.videoWidth) v = this._lastDrawn;
    if (!v || v.readyState < 2 || !v.videoWidth) return false;
    this._lastDrawn = v;
    const vw = v.videoWidth, vh = v.videoHeight;
    const vr = vw / vh, dr = w / h;
    let sw, sh;
    if (vr > dr) { sh = vh; sw = vh * dr; } else { sw = vw; sh = vw / dr; }
    ctx.drawImage(v, (vw - sw) / 2, (vh - sh) / 2, sw, sh, x, y, w, h);
    return true;
  }

  dispose() {
    for (const v of new Set(this.els.values())) {
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
    this.els.clear();
    this.state = null;
    this._lastDrawn = null;
  }
}
